import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, Renderer2, SimpleChanges } from '@angular/core';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CustomTooltipComponent } from '../ui-lib/custom-tooltip/custom-tooltip.component';

@Directive({
    selector: '[appCustomTooltip]',
    standalone: true,
})
export class CustomTooltipDirective implements OnDestroy, OnChanges {
    @Input('appCustomTooltip') tooltipContent = '';

    private overlayRef: OverlayRef | null = null;
    private tooltipInstance: CustomTooltipComponent | null = null;

    constructor(
        private overlay: Overlay,
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private ngZone: NgZone,
    ) {
        this.createTooltip();
        this.setupListeners();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tooltipContent'] && this.tooltipInstance) {
            this.tooltipInstance.content = this.tooltipContent;
        }
    }

    private createTooltip(): void {
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions([
                { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
                { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
                { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
                { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
            ]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
        });

        const tooltipPortal = new ComponentPortal(CustomTooltipComponent);
        const tooltipRef = this.overlayRef.attach(tooltipPortal);
        this.tooltipInstance = tooltipRef.instance;
        this.tooltipInstance.content = this.tooltipContent;

        this.renderer.setStyle(this.overlayRef.overlayElement, 'opacity', '0');
        this.renderer.setStyle(this.overlayRef.overlayElement, 'transition', 'opacity 0.1s');
        this.renderer.setStyle(this.overlayRef.overlayElement, 'pointer-events', 'none');
    }

    private hideTimeout: number | undefined;

    private updateTooltipPosition(): void {
        if (this.overlayRef) {
            const position = this.overlayRef.getConfig().positionStrategy as FlexibleConnectedPositionStrategy;
            position.apply();
        }
    }

    private showTooltip(): void {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }
        if (this.overlayRef) {
            this.updateTooltipPosition();
            this.renderer.setStyle(this.overlayRef.overlayElement, 'opacity', '1');
            this.renderer.setStyle(this.overlayRef.overlayElement, 'pointer-events', 'auto');
        }
    }

    private hideTooltip(): void {
        this.hideTimeout = setTimeout(() => {
            if (this.overlayRef) {
                this.renderer.setStyle(this.overlayRef.overlayElement, 'opacity', '0');
                this.renderer.setStyle(this.overlayRef.overlayElement, 'pointer-events', 'none');
            }
        }, 100); // Задержка в 100 мс
    }

    private setupListeners(): void {
        this.ngZone.runOutsideAngular(() => {
            this.renderer.listen(this.elementRef.nativeElement, 'mouseenter', () => this.showTooltip());
            this.renderer.listen(this.elementRef.nativeElement, 'mouseleave', () => this.hideTooltip());
            this.renderer.listen(this.elementRef.nativeElement, 'mousemove', () => this.updateTooltipPosition());

            if (this.overlayRef) {
                this.renderer.listen(this.overlayRef.overlayElement, 'mouseenter', () => this.showTooltip());
                this.renderer.listen(this.overlayRef.overlayElement, 'mouseleave', () => this.hideTooltip());
            }
        });
    }

    ngOnDestroy(): void {
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }
    }
}
